import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import Pagination from "@/components/base/Pagination";
import { downloadCsv, toCsv } from "@/lib/csv";
import { parseSuppliersCsv, executeImport } from "@/lib/imports";
import StagedImportModal from "@/components/feature/StagedImportModal";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";
import { canManageSuppliers } from "@/lib/permissions";

interface SupplierRow {
  id: string;
  name: string;
  code: string | null;
  status: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  country: string | null;
  payment_terms: string | null;
  lead_time_days: number | null;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const sanitize = (q: string) => q.replace(/[,%_()]/g, "").trim();

export default function Suppliers() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const canManage = canManageSuppliers(role);
  const orgCurrency = org?.currency || "USD";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    code: "",
    contact_name: "",
    email: "",
    phone: "",
    address_line1: "",
    city: "",
    state: "",
    postal_code: "",
    country: "AU",
    currency: orgCurrency,
    payment_terms: "Net 30",
    lead_time_days: "",
    tax_id: "",
    is_preferred: false,
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [importOpen, setImportOpen] = useState(false);

  useCreateDeepLink(() => setOpen(true));

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["suppliers", org?.id, search, statusFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      let q = supabase
        .from("suppliers")
        .select("id, name, code, status, contact_name, email, phone, city, country, payment_terms, lead_time_days", { count: "exact" })
        .eq("org_id", org!.id);
      const term = sanitize(search);
      if (term) {
        const ilike = `%${term}%`;
        q = q.or(`name.ilike.${ilike},code.ilike.${ilike},email.ilike.${ilike}`);
      }
      if (statusFilter) q = q.eq("status", statusFilter);
      q = q.order("name").range(page * pageSize, page * pageSize + pageSize - 1);
      const { data, count } = await q;
      return { rows: (data ?? []) as SupplierRow[], total: count ?? 0 };
    },
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { error: insErr } = await supabase.from("suppliers").insert({
      org_id: org!.id,
      name: form.name,
      code: form.code || null,
      contact_name: form.contact_name || null,
      email: form.email || null,
      phone: form.phone || null,
      address_line1: form.address_line1 || null,
      city: form.city || null,
      state: form.state || null,
      postal_code: form.postal_code || null,
      country: form.country || null,
      currency: form.currency || orgCurrency,
      payment_terms: form.payment_terms || null,
      lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : null,
      tax_id: form.tax_id || null,
      is_preferred: form.is_preferred,
    });
    setSaving(false);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    setOpen(false);
    setForm({ name: "", code: "", contact_name: "", email: "", phone: "", address_line1: "", city: "", state: "", postal_code: "", country: "AU", currency: orgCurrency, payment_terms: "Net 30", lead_time_days: "", tax_id: "", is_preferred: false });
    queryClient.invalidateQueries({ queryKey: ["suppliers", org?.id] });

    // Purchase Order creation embeds the supplier directory in its cached
    // query result. Ensure a newly created supplier is immediately selectable.
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
  };

  const handleExport = async () => {
    const { data } = await supabase
      .from("suppliers")
      .select("id, name, code, status, contact_name, email, phone, city, country, payment_terms, lead_time_days")
      .eq("org_id", org!.id)
      .order("name");
    const rows = (data ?? []) as SupplierRow[];
    const headers = ["name", "code", "contact_name", "email", "phone", "city", "country", "payment_terms", "lead_time_days", "status"];
    const csv = rows.map((s) => [
      s.name, s.code ?? "", s.contact_name ?? "", s.email ?? "", s.phone ?? "", s.city ?? "", s.country ?? "", s.payment_terms ?? "", s.lead_time_days ?? "", s.status,
    ]);
    downloadCsv(`suppliers-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, csv));
  };

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="Manage supplier directory and relationships">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        {canManage && (
          <button onClick={() => setImportOpen(true)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-upload-2-line" /> Import
          </button>
        )}
        {canManage && (
          <button onClick={() => setOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />
            Add Supplier
          </button>
        )}
      </PageHeader>

      {isLoading ? (
        <ListSkeleton />
      ) : isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load suppliers.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState
            icon="ri-truck-line"
            title="No suppliers yet"
            description="Suppliers are the businesses you buy stock from. Add your first one to start purchasing and receiving inventory."
            action={
              canManage ? (
                <button onClick={() => setOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line" /> Add your first supplier
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by name, code or email..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {["active", "inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Supplier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Terms</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows ?? []).map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                      <Link to={`/suppliers/${s.id}`} className="hover:text-emerald-700 hover:underline">{s.name}</Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 font-mono">{s.code ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {s.contact_name ?? "—"}
                      {s.email && <span className="block text-xs text-slate-400">{s.email}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{[s.city, s.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {s.payment_terms ?? "—"}
                      {s.lead_time_days ? <span className="block text-xs text-slate-400">{s.lead_time_days}d lead time</span> : null}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Supplier"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save Supplier"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="TechSource Inc." />
            </div>
            <div>
              <label className={labelCls}>Code</label>
              <input value={form.code} onChange={set("code")} className={inputCls} placeholder="TSI" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Contact name</label>
              <input value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Sarah Chen" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="sarah@supplier.com" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Phone</label>
              <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+61 2 0000 0000" />
            </div>
            <div>
              <label className={labelCls}>Payment terms</label>
              <select value={form.payment_terms} onChange={set("payment_terms")} className={inputCls}>
                <option>Net 15</option>
                <option>Net 30</option>
                <option>Net 60</option>
                <option>Due on receipt</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Lead time (days)</label>
              <input type="number" min="0" value={form.lead_time_days} onChange={set("lead_time_days")} className={inputCls} placeholder="e.g. 7" />
              <p className="text-xs text-slate-400 mt-1">Typical days to receive goods after ordering.</p>
            </div>
          </div>
          <div>
            <label className={labelCls}>Address</label>
            <input value={form.address_line1} onChange={set("address_line1")} className={inputCls} placeholder="123 Supply St" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>City</label>
              <input value={form.city} onChange={set("city")} className={inputCls} placeholder="Sydney" />
            </div>
            <div>
              <label className={labelCls}>State</label>
              <input value={form.state} onChange={set("state")} className={inputCls} placeholder="NSW" />
            </div>
            <div>
              <label className={labelCls}>Postal code</label>
              <input value={form.postal_code} onChange={set("postal_code")} className={inputCls} placeholder="2000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Country</label>
              <input value={form.country} onChange={set("country")} className={inputCls} placeholder="AU" />
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              <select value={form.currency} onChange={set("currency")} className={inputCls}>
                {["USD", "EUR", "GBP", "AUD", "CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Tax ID / VAT</label>
              <input value={form.tax_id} onChange={set("tax_id")} className={inputCls} placeholder="e.g. ABN 12 345 678 901" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.is_preferred} onChange={(e) => setForm((f) => ({ ...f, is_preferred: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
            Preferred supplier
          </label>
        </form>
      </Modal>

      <StagedImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import suppliers"
        description="Import suppliers from a CSV file. Rows are matched by code or email; name-only matches require manual review."
        requiredColumns={["name"]}
        optionalColumns={["code", "contact_name", "email", "phone", "city", "country", "payment_terms", "lead_time_days"]}
        templateHeaders={["name", "code", "contact_name", "email", "phone", "city", "country", "payment_terms", "lead_time_days"]}
        templateRows={[["TechSource Inc.", "TSI", "Sarah Chen", "sarah@supplier.com", "+61 2 0000 0000", "Sydney", "AU", "Net 30", "7"]]}
        onParse={(rows, headers) => parseSuppliersCsv(rows, headers, org!.id)}
        onExecute={(rows) => executeImport("suppliers", org!.id, rows)}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["suppliers", org?.id] })}
      />
    </div>
  );
}